import { Component, type ErrorInfo, type ReactNode, useEffect, useRef, useState } from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useLocation } from "react-router-dom";
import { localeFromPath, localizePath } from "@/i18n/localePath";
import VertLogo from "@/components/VertLogo";
import { utilityRouteCopy } from "@/content/utilityRouteCopy";

export const RouteLoading = () => {
  const locale = typeof window === "undefined" ? "en" : localeFromPath(window.location.pathname);
  const label = utilityRouteCopy.routeExperience[locale].loading;

  return (
    <div
      className="section-dark flex min-h-screen items-center justify-center bg-[hsl(var(--control-ink))] px-6"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <VertLogo className="h-6 w-auto text-[hsl(var(--sd-fg))]" aria-hidden="true" />
        <span className="route-loader-line h-px w-28 overflow-hidden bg-[hsl(var(--sd-fg)/0.2)]" aria-hidden="true">
          <span className="block h-full w-1/2 bg-primary" />
        </span>
        <span className="font-mono text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--sd-fg)/0.78)]">
          {label}
        </span>
      </div>
    </div>
  );
};

export const RouteAccessibility = () => {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");
  const previousPath = useRef<string | null>(null);
  const previousMain = useRef<HTMLElement | null>(null);
  const previousHeading = useRef("");

  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    let cancelled = false;
    const isRouteChange = previousPath.current !== null && previousPath.current !== location.pathname;
    if (previousPath.current === null) {
      const initialMain = document.querySelector<HTMLElement>("#main-content");
      previousMain.current = initialMain;
      previousHeading.current = initialMain?.querySelector("h1")?.textContent?.trim() || "";
    }
    previousPath.current = location.pathname;

    const finishRouteChange = () => {
      if (cancelled) return;
      const main = document.querySelector<HTMLElement>("#main-content");
      const heading = main?.querySelector<HTMLHeadingElement>("h1");
      const headingText = heading?.textContent?.trim() || "";
      const routeContentIsReady = !isRouteChange
        || main !== previousMain.current
        || headingText !== previousHeading.current;

      if ((!main || !heading || !routeContentIsReady) && attempts < 600) {
        attempts += 1;
        frame = window.requestAnimationFrame(finishRouteChange);
        return;
      }

      if (!main) return;

      let hashTargetFocused = false;
      if (location.hash) {
        const hashTarget = document.getElementById(location.hash.slice(1));
        hashTarget?.scrollIntoView();
        if (hashTarget instanceof HTMLElement) {
          hashTarget.focus({ preventScroll: true });
          hashTargetFocused = document.activeElement === hashTarget;
        }
      } else if (isRouteChange) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }

      if (isRouteChange && !hashTargetFocused) {
        main.focus({ preventScroll: true });
      }

      const pageName = headingText || document.title.replace(/\s*\|\s*Vert AI\s*$/, "");
      const locale = localeFromPath(location.pathname);
      const copy = utilityRouteCopy.routeExperience[locale];
      previousMain.current = main;
      previousHeading.current = headingText;
      setAnnouncement(copy.loaded(pageName));
    };

    frame = window.requestAnimationFrame(finishRouteChange);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [location.hash, location.pathname]);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
};

export const RoutePendingIndicator = () => {
  const [pending, setPending] = useState(false);
  const startingMain = useRef<HTMLElement | null>(null);
  const startingHeading = useRef("");
  const startingTitle = useRef("");

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) return;

      const target = event.target;
      const anchor = target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!anchor || anchor.hasAttribute("download") || anchor.target === "_blank") return;

      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.pathname === window.location.pathname) return;

      startingMain.current = document.querySelector<HTMLElement>("#main-content");
      startingHeading.current = startingMain.current?.querySelector("h1")?.textContent?.trim() || "";
      startingTitle.current = document.title;
      setPending(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    let frame = 0;
    const checkForReadyRoute = () => {
      const main = document.querySelector<HTMLElement>("#main-content");
      const heading = main?.querySelector("h1")?.textContent?.trim() || "";
      if (
        main !== startingMain.current
        || heading !== startingHeading.current
        || document.title !== startingTitle.current
      ) {
        setPending(false);
        return;
      }
      frame = window.requestAnimationFrame(checkForReadyRoute);
    };
    frame = window.requestAnimationFrame(checkForReadyRoute);
    const timeout = window.setTimeout(() => setPending(false), 15_000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [pending]);

  if (!pending) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-busy="true">
      <RouteLoading />
    </div>
  );
};

type ErrorBoundaryState = {
  error: Error | null;
};

export class RouteErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) console.error("Route rendering failed", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const locale = typeof window === "undefined" ? "en" : localeFromPath(window.location.pathname);
    const copy = utilityRouteCopy.routeExperience[locale];

    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="section-dark grain flex min-h-screen items-center bg-[hsl(var(--control-ink))] px-5 py-20"
      >
        <div className="mx-auto w-full max-w-2xl border border-[hsl(var(--sd-fg)/0.2)] bg-[hsl(var(--control-panel))] p-7 sm:p-12">
          <p className="mb-5 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            {copy.errorEyebrow}
          </p>
          <h1 className="text-3xl leading-tight text-[hsl(var(--sd-fg))] sm:text-5xl">
            {copy.errorTitle}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-[hsl(var(--sd-fg)/0.78)]">
            {copy.errorBody}
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-primary focus-ring justify-center"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {copy.retry}
            </button>
            <a href={localizePath("/", locale)} className="btn-outline focus-ring justify-center">
              {copy.home}
            </a>
            <a href={localizePath("/contact", locale)} className="btn-outline focus-ring justify-center">
              {copy.contact}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </main>
    );
  }
}
